import { supabase } from '../lib/supabase';
import { v4 as uuidv4 } from 'uuid';
import { db } from '../db';

import { errorMessage } from '../utils/errors';
import { syncvalue } from '../utils/datetime';
import { syncRecordsets, handleRemoteDeletions } from '../utils/sync';

const template = () => {
  const record = {
    id: null,
    facility_id: null,
    species: '',
    behavior_key: '',
    tags: [],
  };

  return { ...record };
};

const transformFromDb = (inputRecord) => {
  const temp = { ...inputRecord };
  temp.tags = Array.isArray(temp.tags) ? temp.tags : [];
  return temp;
};

const transformToDb = (inputRecord) => {
  const temp = { ...inputRecord };
  temp.tags = Array.isArray(temp.tags) ? temp.tags : [];
  return { section_b_behavior_tags: temp };
};

const toDexie = (inputRecord) => {
  const temp = { ...inputRecord };
  temp.tags = Array.isArray(temp.tags) ? temp.tags : [];
  return temp;
};

const fromDexie = (inputRecord) => {
  const temp = { ...inputRecord };
  temp.tags = Array.isArray(temp.tags) ? temp.tags : [];
  return temp;
};

const newBehaviorTagSet = () => {
  return template();
};

const listBehaviorTagSets = async () => {
  try {
    const temp = await db().section_b_behavior_tags.toCollection().toArray();
    return temp.map((cur) => fromDexie(cur));
  } catch (error) {
    console.error(error);
    throw error;
  }
};

const listBehaviorTagSetsBySync = async (lastSync) => {
  try {
    const temp = await db().section_b_behavior_tags.where('sync').aboveOrEqual(lastSync).toArray();
    return temp.map((cur) => fromDexie(cur));
  } catch (error) {
    console.error(error);
    throw error;
  }
};

const getBehaviorTagSet = async (id) => {
  try {
    const temp = await db().section_b_behavior_tags.where('id').equals(id).first();
    return temp ? fromDexie(temp) : undefined;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

const fetchBehaviorTagSets = async () => {
  try {
    const results = await supabase.from('section_b_behavior_tags').select('*');
    if (results.error) throw new Error(`Error ${results.error.code}: ${results.error.message}`);
    if (results.data !== null) {
      return results.data.map((cur) => transformFromDb(cur));
    }
  } catch (error) {
    console.error(error);
    throw error;
  }
};

const fetchBehaviorTagSetsBySync = async (lastSync) => {
  try {
    const results = await supabase.from('section_b_behavior_tags').select('*').gte('sync', lastSync);
    if (results.error) throw new Error(`Error ${results.error.code}: ${results.error.message}`);
    if (results.data !== null) {
      return results.data.map((cur) => transformFromDb(cur));
    }
  } catch (error) {
    console.error(error);
    throw error;
  }
};

const writeBehaviorTagSetToDexie = async (inputRecord) => {
  try {
    const cleanRecord = toDexie(inputRecord);
    const id = await db().section_b_behavior_tags.put(cleanRecord);
    return id;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

const writeBehaviorTagSetToSupabase = async (inputRecord, facilityId) => {
  try {
    inputRecord.facility_id = inputRecord.facility_id || facilityId;
    const { section_b_behavior_tags } = transformToDb(inputRecord);

    const results = await supabase.from('section_b_behavior_tags').upsert(section_b_behavior_tags);
    if (results.error) throw new Error(errorMessage(results.error));
    return { results, id: section_b_behavior_tags.id };
  } catch (error) {
    console.error(error);
    throw error;
  }
};

const dexieDeleteBehaviorTagSet = async (behaviorTagSetId) => {
  try {
    return await db().section_b_behavior_tags.delete(behaviorTagSetId);
  } catch (error) {
    console.error(error);
    throw error;
  }
};

const supabaseDeleteBehaviorTagSet = async (behaviorTagSetId) => {
  try {
    const results = await supabase.from('section_b_behavior_tags').delete().eq('id', behaviorTagSetId);
    if (results.error) throw new Error(errorMessage(results.error));
    return results;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

const deleteBehaviorTagSet = async (behaviorTagSetId, isOnline) => {
  try {
    if (!behaviorTagSetId) throw new Error('behaviorTagSetId is required');
    if (!isOnline) throw new Error('Behavior tag deletion requires an online connection');

    const supabaseResults = await supabaseDeleteBehaviorTagSet(behaviorTagSetId);
    const dexieResults = await dexieDeleteBehaviorTagSet(behaviorTagSetId);

    return { supabaseResults, dexieResults, id: behaviorTagSetId };
  } catch (error) {
    console.error(error);
    throw error;
  }
};

const saveBehaviorTagSet = async (inputRecord, facilityId, isOnline) => {
  const syncValue = syncvalue();

  inputRecord.id = inputRecord.id || uuidv4();
  inputRecord.facility_id = inputRecord.facility_id || facilityId;
  inputRecord.sync = syncValue;

  const dexieResults = await writeBehaviorTagSetToDexie(inputRecord);
  const supabaseResults = isOnline
    ? await writeBehaviorTagSetToSupabase(inputRecord, facilityId)
    : { status: 'offline' };
  return { dexieResults, supabaseResults, id: inputRecord.id };
};

const sync = async ({ facilityId, lastSync = null }) => {
  const deletedRecords = await handleRemoteDeletions({
    tableName: 'section_b_behavior_tags',
    listFunc: listBehaviorTagSets,
    supabase,
    dbTable: db().section_b_behavior_tags,
  });
  console.log('deletedRecords', deletedRecords);

  const { remoteData, localData } = lastSync ? await syncPartialRecordSets(lastSync) : await syncFullRecordSets();
  const results = await syncRecordsets({
    remoteData,
    localData,
    remoteWriteFunc: writeBehaviorTagSetToSupabase,
    localWriteFunc: writeBehaviorTagSetToDexie,
    remoteWriteFuncArgs: [facilityId],
  });
  return results;
};

const syncFullRecordSets = async () => {
  const remoteData = (await fetchBehaviorTagSets()) || [];
  const localData = (await listBehaviorTagSets()) || [];

  return { remoteData, localData };
};

const syncPartialRecordSets = async (lastSync) => {
  const remoteData = (await fetchBehaviorTagSetsBySync(lastSync)) || [];
  const localData = (await listBehaviorTagSetsBySync(lastSync)) || [];

  return { remoteData, localData };
};

const truncateDexie = async () => {
  return await db().section_b_behavior_tags.clear();
};

export {
  newBehaviorTagSet,
  listBehaviorTagSets,
  getBehaviorTagSet,
  fetchBehaviorTagSets,
  saveBehaviorTagSet,
  deleteBehaviorTagSet,
  sync,
  truncateDexie,
};
